import React from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { 
  GraduationCap, 
  Award, 
  Calendar, 
  FileText, 
  CheckCircle, 
  Globe, 
  BookOpen,
  DollarSign,
  Users,
  Clock,
  Building2,
  AlertTriangle
} from "lucide-react";

export default function MusicScholarship() {
  const navigate = useNavigate();

  const handleApplyClick = () => {
    navigate("/login");
  };

  return (
    <div className="space-y-10">
      {/* Hero Section */}
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#182033] via-[#1e293b] to-[#0f172a] p-8 lg:p-12">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 h-32 w-32 rounded-full bg-amber-400 blur-3xl"></div>
          <div className="absolute bottom-10 right-10 h-40 w-40 rounded-full bg-blue-400 blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 h-24 w-24 rounded-full bg-purple-400 blur-2xl"></div>
        </div>
        <div className="relative z-10 text-center">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-amber-600 shadow-lg">
            <GraduationCap className="h-10 w-10 text-white" />
          </div>
          <h1 className="mb-4 text-3xl font-bold tracking-tight text-white lg:text-4xl">
            GMC Academic Scholarship Program
          </h1>
          <p className="mx-auto max-w-2xl text-lg text-[#d5c39d]">
            Empowering talented students worldwide with financial support for their educational journey
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-sm text-slate-300">
            <span className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-amber-400" />
              Open to all nationalities
            </span>
            <span className="flex items-center gap-2">
              <Users className="h-4 w-4 text-amber-400" />
              Ages 10 and above
            </span>
            <span className="flex items-center gap-2">
              <Award className="h-4 w-4 text-amber-400" />
              €300 - €3,000
            </span>
          </div>
        </div>
      </section>

      {/* Program Overview */}
      <section className="space-y-4">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-700">
            <BookOpen className="h-5 w-5" />
          </span>
          <h2 className="text-xl font-semibold text-[#182033]">Program Overview</h2>
        </div>
        <Card className="border-[#e1d2b3] bg-gradient-to-br from-[#fdf8ef] to-white">
          <CardContent className="p-6">
            <div className="space-y-4 text-[#4b4a46]">
              <p className="text-base leading-relaxed">
                The <strong className="text-[#182033]">Global Music Conservatory Academic Scholarship Program</strong> is 
                designed to support aspiring students aged <strong>10 years and above</strong> from 
                <strong> any country in the world</strong>. This scholarship aims to assist students in funding their 
                school or college tuition fees, enabling them to pursue their academic dreams.
              </p>
              <p className="text-base leading-relaxed">
                Over the years, GMC has proudly awarded scholarships to <strong>hundreds of talented students</strong> who 
                demonstrate exceptional abilities in academics and extra-curricular activities. Our goal is to nurture the 
                next generation of talent by removing financial barriers to quality education.
              </p>
              <div className="mt-6 grid gap-4 sm:grid-cols-3">
                <div className="rounded-xl bg-white p-4 text-center shadow-sm border border-[#e1d2b3]">
                  <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <p className="text-2xl font-bold text-[#182033]">100+</p>
                  <p className="text-xs text-[#6b6a66]">Scholarships Awarded</p>
                </div>
                <div className="rounded-xl bg-white p-4 text-center shadow-sm border border-[#e1d2b3]">
                  <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                    <Globe className="h-6 w-6 text-blue-600" />
                  </div>
                  <p className="text-2xl font-bold text-[#182033]">50+</p>
                  <p className="text-xs text-[#6b6a66]">Countries Represented</p>
                </div>
                <div className="rounded-xl bg-white p-4 text-center shadow-sm border border-[#e1d2b3]">
                  <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
                    <Award className="h-6 w-6 text-amber-600" />
                  </div>
                  <p className="text-2xl font-bold text-[#182033]">€3,000</p>
                  <p className="text-xs text-[#6b6a66]">Maximum Award</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Apply Button - Center */}
      <section className="flex justify-center py-4">
        <Button
          onClick={handleApplyClick}
          data-testid="apply-scholarship-top-btn"
          size="lg"
          className="bg-gradient-to-r from-amber-500 to-amber-600 px-10 py-6 text-lg font-semibold text-white shadow-lg hover:from-amber-600 hover:to-amber-700 transition-all hover:scale-105"
        >
          <GraduationCap className="mr-3 h-6 w-6" />
          Apply for Scholarship
        </Button>
      </section>

      {/* Eligibility Criteria */}
      <section className="space-y-4">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-100 text-purple-700">
            <CheckCircle className="h-5 w-5" />
          </span>
          <h2 className="text-xl font-semibold text-[#182033]">Eligibility Criteria</h2>
        </div>
        <Card className="border-[#e1d2b3] bg-white">
          <CardContent className="p-6">
            <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 p-4">
              <p className="text-sm font-medium text-amber-800">
                Selection for the GMC Academic Scholarship is <strong>highly competitive</strong>. 
                Candidates are evaluated based on multiple factors.
              </p>
            </div>
            <div className="space-y-4">
              <h3 className="font-semibold text-[#182033]">Selection is based on:</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">1</span>
                  <div>
                    <p className="font-medium text-[#182033]">Academic Performance</p>
                    <p className="text-sm text-[#6b6a66]">Strong academic records from school or college</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">2</span>
                  <div>
                    <p className="font-medium text-[#182033]">Extra-curricular Activities</p>
                    <p className="text-sm text-[#6b6a66]">Participation in arts, sports, community activities, or other interests</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-bold text-amber-700">★</span>
                  <div>
                    <p className="font-medium text-[#182033]">Scholarship Essay <span className="text-amber-600">(Most Important Factor)</span></p>
                    <p className="text-sm text-[#6b6a66]">A compelling essay explaining why you deserve this scholarship</p>
                  </div>
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Application Fees & Scholarship Amount */}
      <section className="grid gap-6 lg:grid-cols-2">
        {/* Application Fees */}
        <Card className="border-[#e1d2b3] bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-3 text-lg">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100 text-green-700">
                <DollarSign className="h-5 w-5" />
              </span>
              Application Fees
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-xl bg-gradient-to-br from-green-50 to-emerald-50 p-5 text-center border border-green-200">
              <p className="text-3xl font-bold text-green-700">€100</p>
              <p className="text-sm text-green-600">Application Fee</p>
            </div>
            <div className="space-y-3">
              <div className="flex items-start gap-3 rounded-lg bg-green-50 p-3">
                <CheckCircle className="h-5 w-5 shrink-0 text-green-600 mt-0.5" />
                <p className="text-sm text-green-800">
                  <strong>Payable only if the student is selected</strong> for the scholarship
                </p>
              </div>
              <div className="flex items-start gap-3 rounded-lg bg-amber-50 p-3">
                <Clock className="h-5 w-5 shrink-0 text-amber-600 mt-0.5" />
                <p className="text-sm text-amber-800">
                  Must be paid within <strong>10 days</strong> of the scholarship decision email
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Scholarship Amount */}
        <Card className="border-[#e1d2b3] bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-3 text-lg">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                <Award className="h-5 w-5" />
              </span>
              Scholarship Amount
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-xl bg-gradient-to-br from-amber-50 to-yellow-50 p-5 text-center border border-amber-200">
              <p className="text-3xl font-bold text-amber-700">€300 - €3,000</p>
              <p className="text-sm text-amber-600">Award Range</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-[#182033]">Amount depends on:</p>
              <ul className="space-y-2 text-sm text-[#4b4a46]">
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500"></span>
                  Academic background
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500"></span>
                  Your current study area / field / branch
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500"></span>
                  Future study plans
                </li>
              </ul>
            </div>
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-3">
              <p className="text-xs text-blue-800">
                <strong>Note:</strong> Funds will be directly disbursed to the student's school or college.
              </p>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Application Conditions */}
      <section className="space-y-4">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 text-red-700">
            <AlertTriangle className="h-5 w-5" />
          </span>
          <h2 className="text-xl font-semibold text-[#182033]">Application Conditions</h2>
        </div>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-100">
                  <Building2 className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#182033]">Students studying in an accredited university program can only apply</h3>
                  <p className="mt-1 text-sm text-[#4b4a46]">You must be currently enrolled in or accepted to an accredited school, college, or university program.</p>
                </div>
              </div>
              <div className="ml-16 rounded-lg bg-white p-4 border border-red-200">
                <p className="font-medium text-[#182033] mb-2">Requirements:</p>
                <ul className="space-y-2 text-sm text-[#4b4a46]">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Must be enrolled in an <strong>accredited educational institution</strong>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Minimum age: <strong>10 years</strong> at the time of application
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Must provide valid academic records and required documents
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Application Deadlines */}
      <section className="space-y-4">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 text-indigo-700">
            <Calendar className="h-5 w-5" />
          </span>
          <h2 className="text-xl font-semibold text-[#182033]">Application Deadlines</h2>
        </div>
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 mb-4">
          <p className="text-sm text-amber-800">
            <strong>Scholarships are limited</strong> and granted on a first come, first served basis. Apply well in advance of the deadline to get full consideration.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <Card className="border-[#e1d2b3] bg-gradient-to-br from-orange-50 to-amber-50 overflow-hidden">
            <div className="h-2 bg-gradient-to-r from-orange-400 to-amber-400"></div>
            <CardContent className="p-5 text-center">
              <div className="mb-3 inline-flex items-center rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-700">
                Fall Semester
              </div>
              <p className="text-2xl font-bold text-[#182033]">30th April</p>
              <p className="text-sm text-[#6b6a66] mt-1">Last date to apply</p>
              <div className="mt-4 rounded-lg bg-white p-3 border border-orange-200">
                <p className="text-xs text-[#4b4a46]">
                  <strong>Results:</strong> September<br />
                  <strong>Semester:</strong> September intake
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-[#e1d2b3] bg-gradient-to-br from-blue-50 to-indigo-50 overflow-hidden">
            <div className="h-2 bg-gradient-to-r from-blue-400 to-indigo-400"></div>
            <CardContent className="p-5 text-center">
              <div className="mb-3 inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">
                Spring Semester
              </div>
              <p className="text-2xl font-bold text-[#182033]">31st August</p>
              <p className="text-sm text-[#6b6a66] mt-1">Last date to apply</p>
              <div className="mt-4 rounded-lg bg-white p-3 border border-blue-200">
                <p className="text-xs text-[#4b4a46]">
                  <strong>Results:</strong> January<br />
                  <strong>Semester:</strong> January intake
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-[#e1d2b3] bg-gradient-to-br from-green-50 to-emerald-50 overflow-hidden">
            <div className="h-2 bg-gradient-to-r from-green-400 to-emerald-400"></div>
            <CardContent className="p-5 text-center">
              <div className="mb-3 inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">
                Summer Semester
              </div>
              <p className="text-2xl font-bold text-[#182033]">31st December</p>
              <p className="text-sm text-[#6b6a66] mt-1">Last date to apply</p>
              <div className="mt-4 rounded-lg bg-white p-3 border border-green-200">
                <p className="text-xs text-[#4b4a46]">
                  <strong>Results:</strong> May<br />
                  <strong>Semester:</strong> May intake
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Required Documents */}
      <section className="space-y-4">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-teal-100 text-teal-700">
            <FileText className="h-5 w-5" />
          </span>
          <h2 className="text-xl font-semibold text-[#182033]">Required Documents</h2>
        </div>
        <Card className="border-[#e1d2b3] bg-white">
          <CardContent className="p-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-3">
                <div className="flex items-start gap-3 rounded-lg bg-slate-50 p-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-teal-100 text-sm font-bold text-teal-700">1</span>
                  <div>
                    <p className="font-medium text-[#182033]">Age Proof</p>
                    <p className="text-xs text-[#6b6a66]">Government-issued ID (passport, birth certificate, etc.)</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-lg bg-slate-50 p-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-teal-100 text-sm font-bold text-teal-700">2</span>
                  <div>
                    <p className="font-medium text-[#182033]">Academic Records</p>
                    <p className="text-xs text-[#6b6a66]">School/college grades, marksheets, and transcripts</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-lg bg-slate-50 p-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-teal-100 text-sm font-bold text-teal-700">3</span>
                  <div>
                    <p className="font-medium text-[#182033]">Work Experience</p>
                    <p className="text-xs text-[#6b6a66]">If any, with supporting proof/documentation</p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-start gap-3 rounded-lg bg-amber-50 p-3 border border-amber-200">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-200 text-sm font-bold text-amber-700">4</span>
                  <div>
                    <p className="font-medium text-[#182033]">Scholarship Essay</p>
                    <p className="text-xs text-[#6b6a66]">Minimum <strong>500 words</strong></p>
                    <p className="text-xs text-amber-700 mt-1 font-medium">Topic: "Why I deserve this scholarship"</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-lg bg-slate-50 p-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-teal-100 text-sm font-bold text-teal-700">5</span>
                  <div>
                    <p className="font-medium text-[#182033]">Student Resume <span className="text-slate-400 text-xs">(Optional)</span></p>
                    <p className="text-xs text-[#6b6a66]">PDF format with your background and achievements</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Bottom CTA */}
      <section className="rounded-2xl bg-gradient-to-r from-[#182033] to-[#1e3a5f] p-8 text-center">
        <h2 className="mb-4 text-2xl font-bold text-white">Ready to Apply?</h2>
        <p className="mx-auto mb-6 max-w-xl text-[#d5c39d]">
          Take the first step towards your educational journey. Sign up for a student account and apply today!
        </p>
        <Button
          onClick={handleApplyClick}
          data-testid="apply-scholarship-bottom-btn"
          size="lg"
          className="bg-amber-500 px-8 py-6 text-lg font-semibold text-white hover:bg-amber-600"
        >
          <GraduationCap className="mr-2 h-5 w-5" />
          Apply for Scholarship
        </Button>
      </section>
    </div>
  );
}
